/**
 * Browser notifications for chat turns that finish while the tab is unfocused.
 *
 * Deliberately React-free so the branches that matter (permission state, focus
 * state, feature detection) are testable without a render harness. The React
 * wiring lives in useChatNotifications.
 *
 * Two rules shape this module:
 *
 * 1. `Notification.requestPermission()` is never called except from a click on
 *    our own UI. A native "Block" is PERMANENT — it cannot be re-requested, and
 *    the browser offers no in-app path back. A decline on our own prompt costs
 *    nothing, so the soft-ask is what we show and the native prompt is what the
 *    user's "yes" opts into.
 * 2. Notification bodies carry no chat content. This is a multi-tenant platform
 *    and an OS notification renders on lock screens and shared displays, well
 *    outside the entitlement boundary every other read goes through.
 */

const NOTIFY_PREF_KEY = "ranger-chat-notify-on-complete";
const SOFT_ASK_DECLINES_KEY = "ranger-chat-notify-soft-ask-declines";

/** Stop offering after this many declines; the settings tab remains the way in. */
const MAX_SOFT_ASK_DECLINES = 2;

export type NotificationPermissionState =
  | "unsupported"
  | "default"
  | "granted"
  | "denied";

/**
 * Whether this browser can show a notification from page script.
 *
 * Android Chrome exposes `window.Notification` but THROWS TypeError from the
 * constructor — it only supports notifications via a service worker's
 * registration.showNotification, and this app registers no service worker. The
 * constructor call is additionally wrapped in try/catch at the call site,
 * because this check cannot detect that case up front.
 */
export function notificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function getNotificationPermission(): NotificationPermissionState {
  if (!notificationsSupported()) return "unsupported";
  return Notification.permission as NotificationPermissionState;
}

/**
 * The user's own on/off switch, independent of the browser permission.
 *
 * Defaults to ON: the browser permission is the real gate, and a user who has
 * granted it has already said yes. This exists so they can turn notifications
 * off WITHOUT revoking the permission — revoking is the irreversible door.
 */
export function loadNotifyPreference(): boolean {
  try {
    const stored = localStorage.getItem(NOTIFY_PREF_KEY);
    return stored === null ? true : stored === "1";
  } catch {
    return true;
  }
}

export function saveNotifyPreference(enabled: boolean): void {
  try {
    localStorage.setItem(NOTIFY_PREF_KEY, enabled ? "1" : "0");
  } catch {
    /* Safari private mode throws on setItem; the preference is not worth failing over. */
  }
}

function loadSoftAskDeclines(): number {
  try {
    return Number(localStorage.getItem(SOFT_ASK_DECLINES_KEY)) || 0;
  } catch {
    return 0;
  }
}

export function recordSoftAskDeclined(): void {
  try {
    localStorage.setItem(
      SOFT_ASK_DECLINES_KEY,
      String(loadSoftAskDeclines() + 1)
    );
  } catch {
    /* ignore */
  }
}

/**
 * A turn finished while the tab was unfocused and we could not notify.
 *
 * In-memory rather than persisted: the trigger recurs naturally, and asking on
 * a fresh page load is asking without the context that motivates the answer.
 */
let missedCompletion = false;

export function markMissedCompletion(): void {
  missedCompletion = true;
}

/** Test seam. */
export function resetMissedCompletion(): void {
  missedCompletion = false;
}

/**
 * Whether to show the soft-ask, checked when the user returns to the tab.
 *
 * "denied" is excluded deliberately — re-prompting is impossible, so the offer
 * would be a dead button. The settings tab explains the site-settings route
 * instead.
 */
export function shouldOfferNotifications(): boolean {
  if (!missedCompletion) return false;
  if (getNotificationPermission() !== "default") return false;
  if (!loadNotifyPreference()) return false;
  return loadSoftAskDeclines() < MAX_SOFT_ASK_DECLINES;
}

/**
 * Ask the browser. MUST be called from a user gesture handler: Safari and
 * Firefox reject the request without one, and Chrome degrades it to a muted
 * prompt the user is unlikely to see.
 */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!notificationsSupported()) return "unsupported";
  missedCompletion = false;
  try {
    return (await Notification.requestPermission()) as NotificationPermissionState;
  } catch {
    // Older Safari only has the callback form and rejects the promise form.
    return getNotificationPermission();
  }
}

export interface TurnCompleteNotification {
  sessionId: string;
  outcome: "done" | "error";
  /** Bring the user to this conversation when they click through. */
  onActivate: () => void;
}

/**
 * Notify that a turn ended, if the tab is unfocused and we are allowed to.
 *
 * `document.hasFocus()` rather than `visibilityState`: a visible tab sitting
 * beside a focused editor is exactly the case this feature is for, and
 * visibilityState reports that tab as visible.
 */
export function notifyTurnComplete({
  sessionId,
  outcome,
  onActivate,
}: TurnCompleteNotification): void {
  if (typeof document === "undefined" || document.hasFocus()) return;

  if (
    !notificationsSupported() ||
    getNotificationPermission() !== "granted" ||
    !loadNotifyPreference()
  ) {
    // They missed a completion and we could not tell them. If the permission is
    // still "default" this is what earns the soft-ask on their return.
    markMissedCompletion();
    return;
  }

  try {
    const notification = new Notification(
      outcome === "error" ? "Sirius chat stopped" : "Sirius chat finished",
      {
        // No chat content — see the module docstring.
        body:
          outcome === "error"
            ? "Something went wrong with your request."
            : "Your response is ready.",
        // Replaces rather than stacks, so one conversation can never pile up.
        // Distinct conversations keep distinct tags and notify separately.
        tag: `ranger-chat-${sessionId}`,
      }
    );
    notification.onclick = () => {
      window.focus();
      onActivate();
      notification.close();
    };
  } catch {
    // Android Chrome throws TypeError from the constructor (service worker
    // required). Missing a notification must never break the stream handler.
  }
}
